import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Users, Crown, Shield, DollarSign, TrendingUp, UserPlus, Loader2,
  Trophy, Gift, Building2, BarChart3, Activity
} from 'lucide-react';

import { AdminBotsTab } from '../components/admin/AdminBotsTab';
import { AdminPricesTab } from '../components/admin/AdminPricesTab';
import { AdminDividendsTab } from '../components/admin/AdminDividendsTab';
import { AdminAdsTab } from '../components/admin/AdminAdsTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminAffiliatesTab } from '../components/admin/AdminAffiliatesTab';
import { AdminTenantsTab } from '../components/admin/AdminTenantsTab';
import { AdminHealthTab } from '../components/admin/AdminHealthTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ADMIN_TABS = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'health', label: 'Health', icon: Activity },
  { id: 'bots', label: 'Bots', icon: Trophy },
  { id: 'prices', label: 'Prices', icon: BarChart3 },
  { id: 'dividends', label: 'Dividends', icon: DollarSign },
  { id: 'ads', label: 'Advertising', icon: TrendingUp },
  { id: 'affiliates', label: 'Affiliates', icon: Gift },
  { id: 'tenants', label: 'Brokers/Tenants', icon: Building2 },
];

const StatCard = ({ label, value, icon, color, subtext }) => {
  const colorClasses = {
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    green: 'bg-green-500/10 text-green-400 border-green-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  };
  return (
    <Card className={`${colorClasses[color]} border`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-400 text-sm">{label}</span>
          {icon}
        </div>
        <div className="text-2xl font-bold text-white">{value}</div>
        {subtext && <div className="text-sm mt-1 opacity-75">{subtext}</div>}
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeAdminTab, setActiveAdminTab] = useState('users');
  const [pendingPayoutsCount, setPendingPayoutsCount] = useState(0);

  useEffect(() => { verifyAdmin(); }, [token]);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchPendingPayoutsCount();
    }
  }, [isAdmin]);

  const verifyAdmin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsAdmin(res.ok);
    } catch { setIsAdmin(false); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error('Error fetching stats:', err); }
  };

  const fetchPendingPayoutsCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/affiliate/admin/payouts/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingPayoutsCount((data.payouts || []).length);
      }
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Card className="bg-slate-900/50 border-slate-800 max-w-md">
          <CardContent className="py-12 text-center">
            <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-slate-400">You don't have permission to access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="h-8 w-8 text-red-400" />
              Admin Dashboard
            </h1>
            <p className="text-slate-400 mt-1">Manage users and monitor platform activity</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Users" value={stats.total_users} icon={<Users className="h-5 w-5" />} color="blue" />
            <StatCard label="Pro Members" value={stats.pro_users} icon={<Crown className="h-5 w-5" />} color="yellow" subtext={`${stats.conversion_rate}% conversion`} />
            <StatCard label="New This Week" value={stats.recent_signups} icon={<UserPlus className="h-5 w-5" />} color="green" />
            <StatCard label="Revenue" value={`$${stats.total_revenue?.toFixed(2) || '0.00'}`} icon={<DollarSign className="h-5 w-5" />} color="emerald" />
          </div>
        )}

        {/* Admin Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-800 pb-4">
          {ADMIN_TABS.map(tab => (
            <Button
              key={tab.id}
              variant={activeAdminTab === tab.id ? 'default' : 'ghost'}
              onClick={() => setActiveAdminTab(tab.id)}
              className={activeAdminTab === tab.id ? 'bg-emerald-600' : 'text-slate-400'}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
              {tab.id === 'affiliates' && pendingPayoutsCount > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white">{pendingPayoutsCount}</Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeAdminTab === 'users' && <AdminUsersTab token={token} onStatsRefresh={fetchStats} />}
        {activeAdminTab === 'health' && <AdminHealthTab token={token} />}
        {activeAdminTab === 'bots' && <AdminBotsTab token={token} />}
        {activeAdminTab === 'prices' && <AdminPricesTab token={token} />}
        {activeAdminTab === 'dividends' && <AdminDividendsTab token={token} />}
        {activeAdminTab === 'ads' && <AdminAdsTab token={token} />}
        {activeAdminTab === 'affiliates' && <AdminAffiliatesTab token={token} />}
        {activeAdminTab === 'tenants' && <AdminTenantsTab token={token} />}
      </div>
    </div>
  );
};

export default AdminDashboard;
